import { useEffect, useMemo, useState } from "react";
import { Clock, Mail, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";

const TEXT = {
  it: {
    seoTitle: "News & Insights Fiscali e Societari | Mediolanum Consulting",
    seoDesc: "Analisi giuridiche e fiscali firmate dallo studio: riforme, giurisprudenza, M&A e operazioni di mercato commentate con taglio operativo.",
    eyebrow: "News & Insights",
    copy: "La rassegna tecnica dello studio: riforme, giurisprudenza e operazioni di mercato commentate con taglio operativo.",
    all: "Tutti",
    loading: "Caricamento degli articoli…",
    error: "Impossibile caricare gli articoli",
    nlEyebrow: "Mediolanum Financial Review",
    nlTitle: "La newsletter istituzionale dello studio.",
    nlCopy: "Una edizione mensile con le novità fiscali e societarie che contano davvero. Riservata a imprenditori e CFO.",
    nlPlaceholder: "La vostra email aziendale",
    nlBtn: "Iscriviti",
    nlOk: "Iscrizione confermata",
    nlOkDesc: "Riceverete la prossima edizione della Financial Review.",
    modalFooter: "Mediolanum Consulting Srl — Ufficio Studi",
    readSuffix: "di lettura",
    locale: "it-IT",
    langNote: null,
  },
  en: {
    seoTitle: "Tax & Corporate News and Insights | Mediolanum Consulting",
    seoDesc: "Legal and tax analysis by the firm: reforms, case law, M&A and market transactions with an operational perspective.",
    eyebrow: "News & Insights",
    copy: "The firm's technical review: reforms, case law and market transactions with an operational perspective.",
    all: "All",
    loading: "Loading articles…",
    error: "Unable to load articles",
    nlEyebrow: "Mediolanum Financial Review",
    nlTitle: "The firm's institutional newsletter.",
    nlCopy: "A monthly edition with the tax and corporate news that really matters. Reserved for entrepreneurs and CFOs.",
    nlPlaceholder: "Your business email",
    nlBtn: "Subscribe",
    nlOk: "Subscription confirmed",
    nlOkDesc: "You will receive the next edition of the Financial Review.",
    modalFooter: "Mediolanum Consulting Srl — Research Office",
    readSuffix: "read",
    locale: "en-GB",
    langNote: "Articles are currently published in Italian by our research office.",
  },
};

const TOPIC_KEYS = ["Fisco", "M&A", "Governance", "Giurisprudenza"];

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [topic, setTopic] = useState("tutti");
  const [open, setOpen] = useState(null);
  const [nlEmail, setNlEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();
  const c = TEXT[lang];

  useEffect(() => {
    api
      .get("/insights")
      .then((res) => setArticles(res.data))
      .catch(() => toast.error(c.error))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(
    () => (topic === "tutti" ? articles : articles.filter((a) => a.topic === topic)),
    [articles, topic]
  );

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", {
        nome: "Iscrizione Newsletter",
        email: nlEmail,
        messaggio: "Richiesta di iscrizione alla Mediolanum Financial Review.",
        tipo_servizio: "newsletter",
      });
      toast.success(c.nlOk, { description: c.nlOkDesc });
      setNlEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(c.locale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <main data-testid="insights-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section className="navy-mesh mx-auto max-w-7xl px-5 pb-14 pt-44 sm:px-8">
        <SectionHeading
          level={1}
          eyebrow={c.eyebrow}
          title={lang === "en"
            ? <>Legal and tax analysis, <span className="text-gold-gradient italic">signed by the partners.</span></>
            : <>Analisi giuridiche e fiscali, <span className="text-gold-gradient italic">firmate dai partner.</span></>}
          copy={c.copy}
        />
        <div className="mt-10 flex flex-wrap gap-3" data-testid="insights-filter">
          {["tutti", ...TOPIC_KEYS].map((tk) => (
            <button
              key={tk}
              onClick={() => setTopic(tk)}
              data-testid={`insights-filter-${tk.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-full border px-5 py-2.5 text-sm transition-all duration-300 ${
                topic === tk ? "border-gold bg-gold font-semibold text-navy-950" : "border-white/15 text-slate-300 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {tk === "tutti" ? c.all : t(tk)}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        {lang === "en" && c.langNote && (
          <p className="mb-8 rounded-xl border border-gold/20 bg-gold/5 px-5 py-4 text-sm font-light text-slate-400" data-testid="insights-lang-note">
            {c.langNote}
          </p>
        )}
        {loading ? (
          <p className="py-16 text-center font-mono text-sm text-slate-500" data-testid="insights-loading">{c.loading}</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
            {visible.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.08}>
                <button
                  onClick={() => setOpen(a)}
                  data-testid={`insight-card-${i}`}
                  className="card-surface card-surface-hover group block h-full w-full overflow-hidden rounded-2xl text-left"
                >
                  {a.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={a.image_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-7">
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                      <span>{t(a.topic)}</span>
                      <span className="h-1 w-1 rotate-45 bg-gold/60" />
                      <span className="flex items-center gap-1 text-slate-500"><Clock size={11} /> {a.read_time}</span>
                    </div>
                    <h3 className="mt-3 font-serif text-xl leading-snug text-slate-100">{a.title}</h3>
                    <p className="mt-3 line-clamp-3 text-sm font-light text-slate-400">{a.excerpt}</p>
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {fmtDate(a.published_at)}
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <Reveal>
          <div className="card-surface relative overflow-hidden rounded-2xl p-8 sm:p-12" data-testid="newsletter-section">
            <div className="navy-mesh absolute inset-0" />
            <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
              <div>
                <p className="eyebrow">{c.nlEyebrow}</p>
                <h3 className="mt-4 font-serif text-2xl text-slate-100 sm:text-3xl">{c.nlTitle}</h3>
                <p className="mt-3 text-sm font-light text-slate-400">{c.nlCopy}</p>
              </div>
              <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
                <div className="relative flex-1">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="email"
                    required
                    value={nlEmail}
                    onChange={(e) => setNlEmail(e.target.value)}
                    placeholder={c.nlPlaceholder}
                    data-testid="newsletter-email-input"
                    className="w-full rounded-full border border-white/15 bg-navy-900 py-3.5 pl-11 pr-4 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-gold"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="newsletter-submit-btn"
                  className="rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light"
                >
                  {c.nlBtn}
                </button>
              </form>
            </div>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-950/85 p-4 backdrop-blur-md"
            onClick={() => setOpen(null)}
            data-testid="insight-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="card-surface max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {open.image_url && (
                <div className="h-56 overflow-hidden sm:h-72">
                  <img src={open.image_url} alt={open.title} className="h-full w-full object-cover" />
                </div>
              )}
              <div className="p-8 sm:p-12">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{t(open.topic)} · {open.read_time} {c.readSuffix}</p>
                    <h2 className="mt-3 font-serif text-2xl leading-snug text-slate-100 sm:text-3xl">{open.title}</h2>
                  </div>
                  <button
                    onClick={() => setOpen(null)}
                    data-testid="insight-modal-close"
                    className="rounded-full border border-white/15 p-2 text-slate-400 transition-colors hover:border-gold hover:text-gold"
                    aria-label="Chiudi articolo"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="mt-8 space-y-5">
                  {open.content.split("\n\n").map((p, i) => (
                    <p key={i} className="text-base font-light leading-relaxed text-slate-300">{p}</p>
                  ))}
                </div>
                <p className="mt-10 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {c.modalFooter} · {fmtDate(open.published_at)}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
